import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  ICreateExamQuestion,
  IExamQuestion,
  IExamQuestionRaw,
  IGenericError,
} from '../types'

const examQuestionsCollectionName = 'examQuestions'

const examQuestionsCollectionRef = collection(
  firebaseDb,
  examQuestionsCollectionName,
)

const examQuestionsDocRef = (id: string) =>
  doc(firebaseDb, examQuestionsCollectionName, id)

export const getExamQuestionById = async (
  id: string,
): Promise<IExamQuestion | undefined> => {
  try {
    const docRef = examQuestionsDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IExamQuestionRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IExamQuestion = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(`error while tying to get examResource by id: ${id} :>> `, {
      message: (error as IGenericError)?.message,
      error,
    })
  }
}

export async function getExamQuestions(
  queryConstraints: QueryConstraint[] = [],
): Promise<IExamQuestion[]> {
  try {
    let q = query(examQuestionsCollectionRef, ...queryConstraints)

    const querySnapshot = (await getDocs(q)) as QuerySnapshot<IExamQuestionRaw>

    let examQuestions: IExamQuestion[] = []

    querySnapshot.forEach((doc) => {
      examQuestions.push({
        id: doc.id,
        ...doc.data(),
      })
    })

    return examQuestions
  } catch (error: unknown) {
    console.error(`error while trying to get examQuestions :>> `, {
      message: (error as IGenericError)?.message,
      error,
      queryConstraints,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get examQuestions`,
    )
  }
}

export async function getExamQuestionsListByIds(
  ids: string[],
): Promise<IExamQuestion[]> {
  try {
    const questions: IExamQuestion[] = await Promise.all(
      ids.map(async (id) => {
        return await getExamQuestionById(id)
      }),
    ).then((data) =>
      // filter out undefined values
      data.filter((q): q is IExamQuestion => q !== undefined),
    )

    return questions
  } catch (error: unknown) {
    console.error(`error while trying to get examQuestions list :>> `, {
      message: (error as IGenericError)?.message,
      error,
      ids,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get examQuestions list.`,
    )
  }
}

export async function setExamQuestionById(
  id: string,
  question: ICreateExamQuestion,
): Promise<void> {
  try {
    await setDoc(examQuestionsDocRef(id), {
      ...question,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error(
      `error while trying to set exam question by id: ${id} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to set exam question by id`,
    )
  }
}
